import React, { Fragment, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { ConfirmActions } from "src/redux/Confirm/actions";
import { ConfirmSelectors } from "src/redux/Confirm/selectors";

import Button from "../../common/Button";
import styles from "./styles.module.scss";
import CheckBox from "src/components/common/CheckBox";

/*
Usage :
   openConfirm({message: "Message", title: "Notice",
                okTitle: "YES", cancelTitle: "NO", onOK: () => alert("ok")});
   title: optional
   message: required
   subMessage: optional
   [These options are available if onOk exists]
   onOk: optional. If null, only single 'OK' button will be shown and it just closes the window
   okTitle: optional. If empty, the OK button name will be 'OK'
   cancelTitle: optional. If empty, the Cancel button name will be "Cancel"
*/

const isFunction = (func) => {
    return func && func.constructor && func.call && func.apply;
};

const Confirm = (props) => {
    const dispatch = useDispatch();
    const isOpen = useSelector(ConfirmSelectors.IsOpen);
    const options = useSelector(ConfirmSelectors.Options);
    const [description, setDescription] = useState("");
    const [checked, setChecked] = useState(false);
    const onClose = () => {
        setDescription("");
        setChecked(false);
        dispatch(ConfirmActions.Close());
    };
    if (!isOpen) return null;

    return (
        <Fragment>
            <div
                className={`${styles.confirmWrapper} ${
                    options.backdrop ? styles.backdrop : ""
                }`}
                data-testid={options.testId}
            >
                <div className={styles.confirm}>
                    <Header onClose={onClose} />
                    <Body
                        options={options}
                        description={description}
                        setDescription={setDescription}
                        checked={checked}
                        setChecked={setChecked}
                        onClose={onClose}
                    />
                </div>
            </div>
        </Fragment>
    );
};
const Body = ({ options = {}, onClose, description, setDescription, checked, setChecked }) => {
    const [inputLength, setInputLength] = useState(description.length);

    const handleChange = (e) => {
        const text = e.target.value;
        setDescription(text);
        setInputLength(text.length);
    };

    const isInputWithinMax = inputLength <= 100;
    const isInputAboveMin = !options.isDescriptionRequired || inputLength > 0;
    const isInputValid = isInputWithinMax && isInputAboveMin;

    return (
        <div className={styles.body}>
            {options.title && (
                <div
                    className={`${styles.title} ${
                        options.warning ? styles.warning : ""
                    }`}
                >
                    {options.title}
                </div>
            )}
            <div
                className={
                    options.title ? styles.messageWithTitle : styles.message
                }
            >
                {options.message.split("\n").map((m, i) => {
                    return (
                        <span key={`text-${i}`}>
                            {m}
                            <br />
                        </span>
                    );
                })}
            </div>
            {options.subMessage && (
                <div className={styles.subMessage}>
                    {options.subMessage.split("\n").map((m, i) => {
                        return (
                            <span key={`text-${i}`}>
                                {m}
                                <br />
                            </span>
                        );
                    })}
                </div>
            )}
            {options.getDescription && (
                <div className={styles.text_area}>
                    <div className={styles.text_left}>Comment</div>
                    {options.isDescriptionRequired ? (
                        <div className={styles.text_right}>(Required)</div>
                    ) : (
                        <div className={styles.text_right}>(Optional)</div>
                    )}

                    <textarea
                        rows={4}
                        data-testid="confirm-textarea"
                        value={description}
                        placeholder={options.descriptionPlaceholder || ""}
                        onChange={handleChange}
                    />
                    {!isInputWithinMax && (
                        <div className={styles.error}>
                            Comment length must not exceed 100 characters
                        </div>
                    )}
                    {!isInputAboveMin && (
                        <div className={styles.error}>Comment is required</div>
                    )}
                </div>
            )}
            {options.checkboxLabel && (
                <div className={styles.checkboxBlock}>
                    <div className={styles.checkboxRow} data-testid="confirm-checkbox-row">
                        <CheckBox
                            name="confirm-checkbox"
                            selected={checked}
                            onChange={() => setChecked(!checked)}
                            dataTestId="confirm-checkbox"
                            small
                            styleName="mint"
                            ariaLabel={options.checkboxLabel}
                        >
                            {options.checkboxLabel}
                        </CheckBox>
                    </div>
                    {options.checkboxNote && (
                        <div className={styles.checkboxNote}>{options.checkboxNote}</div>
                    )}
                </div>
            )}
            <ButtonArea
                options={options}
                onClose={onClose}
                description={description}
                checked={checked}
                isInputValid={isInputValid}
            />
        </div>
    );
};

const Header = ({ onClose }) => {
    return (
        <div className={styles.header}>
            <div className={styles.close} onClick={onClose} />
        </div>
    );
};
const ButtonArea = ({ options = {}, onClose, description, checked, isInputValid }) => {
    return (
        <div className={styles.btnArea}>
            {isFunction(options.onOk) ? (
                <>
                    <Button
                        value={options.cancelTitle || "Cancel"}
                        onClick={() => {
                            if (isFunction(options.onCancel))
                                options.onCancel();
                            onClose();
                        }}
                        dataTestId={`confirm_${
                            options.cancelTitle
                                ? options.cancelTitle.toLowerCase()
                                : "cancel"
                        }`}
                        color="reversedWhite"
                    />
                    <Button
                        value={options.okTitle || "OK"}
                        onClick={() => {
                            options.onOk(description, checked);
                            onClose();
                        }}
                        dataTestId={`confirm_${
                            options.okTitle
                                ? options.okTitle.toLowerCase()
                                : "ok"
                        }`}
                        autoFocus={true}
                        disabled={!isInputValid}
                    />
                </>
            ) : (
                <Button
                    value="OK"
                    onClick={onClose}
                    dataTestId="confirm_ok"
                    autoFocus={true}
                    disabled={!isInputValid}
                />
            )}
        </div>
    );
};

export default Confirm;
